import type { ReactNode } from "react";

interface StepHeaderProps {
  eyebrow: string;
  title: ReactNode;
  lede?: ReactNode;
}

/** Shared eyebrow/title/lede block reused by every onboarding screen. */
export function StepHeader({ eyebrow, title, lede }: StepHeaderProps) {
  return (
    <div>
      <span className="text-[12px] font-medium uppercase tracking-[0.16em] text-primary">{eyebrow}</span>
      <h1 className="mt-3.5 mb-2.5 font-display text-[clamp(32px,3.4vw,44px)] font-light leading-[1.15] tracking-[-0.005em] text-foreground">
        {title}
      </h1>
      {lede && <p className="mb-[30px] text-[15px] leading-[1.65] text-fg-muted">{lede}</p>}
    </div>
  );
}

/**
 * The onboarding's primary action, the prototype's `.btn-lg`: 13px (`--fs-body-sm`), `.16em`
 * tracking, 18px block padding — 52px tall, not the DS `lg` button's 48px.
 */
export const STEP_CTA_CLASS = "h-[52px] text-[13px] tracking-[0.16em]";

/**
 * For a CTA whose words can outgrow a phone's width: the prototype's `.btn` has no `nowrap`, so
 * the label breaks onto a second line instead of running past the edges. 52px on one line, as
 * `STEP_CTA_CLASS`; taller on two. Measured on 2026-10-26: "Salvar e seguir para o regulamento"
 * ran 12px past the button at 360px wide and 32px at 320px.
 */
export const STEP_CTA_WRAP_CLASS = "h-auto min-h-[52px] whitespace-normal py-[17px] text-center leading-[18px]";

/**
 * The step's way back (`.backlink`), over the ghost `sm` button: the onboarding's `BackLink` and
 * the list creation's link share it. Here and not in `back-link.tsx`, a client module, so a
 * server component can read the string.
 */
export const BACK_LINK_CLASS =
  "mb-[22px] h-auto w-fit gap-[7px] p-0 text-[12px] normal-case tracking-[0.06em] text-fg-muted hover:bg-transparent hover:text-primary";

/** Highlights a word/phrase in the title, brand-style — no italics. */
export function Emphasis({ children }: { children: ReactNode }) {
  return <span className="text-primary">{children}</span>;
}

/**
 * "· opcional" suffix inside a field label — normal-case and tighter-tracked on purpose, so
 * it does not shout like the uppercase, wide-tracked label around it.
 */
export function OptionalHint() {
  return <span className="font-medium normal-case tracking-[0.01em] text-fg-muted"> · opcional</span>;
}

// TODO(DS): replace with @tb/icon when DS icon set is available
export function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="size-4">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}
